<script setup>
  import { ref, computed, useSlots } from 'vue';

  import CredereSpinner from '@/components/Core/Spinner.vue';

  const slots = useSlots();

  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },

    size: {
      type: String,
      default: 'medium',
      required: false,
      validator(value) {
        return ['small', 'medium', 'large'].includes(value);
      },
    },
    color: {
      type: String,
      default: 'primary',
      required: false,
      validator(value) {
        return ['primary', 'secondary', 'highlight', 'danger', 'light'].includes(value);
      },
    },

    href: {
      type: String,
      default: null,
      required: false,
    },

    variant: {
      type: String,
      default: 'A',
      required: false,
      validator(value) {
        return ['A', 'B', 'C'].includes(value);
      },
    },

    variantSecondary: {
      type: String,
      default: 'A',
      required: false,
      validator(value) {
        return ['A', 'B'].includes(value);
      },
    },

    // An icon-only button has no accessible name of its own: it needs either this or a
    // `title`. Text buttons take their name from the slot.
    label: {
      type: String,
      default: null,
      required: false,
    },

    icon: {
      type: String,
      required: false,
    },
    iconPosition: {
      type: String,
      default: 'left',
      required: false,
      validator(value) {
        return ['left', 'right'].includes(value);
      },
    },
    iconSize: {
      type: String,
      default: 'medium',
      required: false,
      validator(value) {
        return ['medium', 'large'].includes(value);
      },
    },
  });

  const emit = defineEmits(['click', 'blur', 'focus']);

  /*
   * With `href` the component renders an `<a>`, and there `disabled` is a meaningless
   * attribute: the element stays clickable, focusable and navigating, so the guard lives here.
   */
  function click(e) {
    if (props.disabled || props.loading) {
      e.preventDefault();
      e.stopPropagation();

      return;
    }

    emit('click', e);
  }

  function focus(e) {
    emit('focus', e);
  }

  function blur(e) {
    emit('blur', e);
  }

  const { button_primary: _primary, button_secondary: _secondary } = window.CREDERE.variants || {};
  const variantPrimary = computed(() => _primary || props.variant);
  const variantSecondary = computed(() => _secondary || props.variantSecondary);

  const classes = computed(() => ({
    'credere-button': true,

    [`size--${props.size}`]: true,
    [`color--${props.color}`]: true,

    [`icon-size--${props.iconSize}`]: true,
    [`icon-position--${props.iconPosition}`]: true,

    'state--disabled': props.disabled,
    'state--loading': props.loading,

    'variant--iconed': props.icon && !slots.default,
    [`variant--button-1${variantPrimary.value?.toLowerCase()}`]: variantPrimary.value,
    [`variant--button-2${variantSecondary.value?.toLowerCase()}`]: props.color === 'secondary' && variantSecondary.value,
  }));

  /*
   * The spinner dot is picked by the button's background, which is why this is a table
   * and not a condition.
   *
   * While loading, `state--loading` zeroes the text color: the three dots are the only
   * sign left on screen, and a dot in the background color is no sign at all. Button
   * used to pass a fixed `light`, which left the light button loading with nothing visible.
   *
   * Each color's pair came from measuring the contrast of the available tones against
   * each button's background, at the animation's trough - the dots drop to 50% opacity
   * mid-cycle, and that is where contrast is at its worst. In all five colors the winner
   * at the trough is also the winner at full dot, so there is no tie to break. The table
   * with the numbers is in the `Carregando` story.
   */
  const SPINNER_VARIANTS = {
    primary: 'light',
    secondary: 'light',
    danger: 'light',
    highlight: 'dark',
    light: 'dark',
  };

  const spinnerVariant = computed(() => SPINNER_VARIANTS[props.color]);

  const refButton = ref();
  defineExpose({ refButton });
</script>

<template>
  <component
    ref="refButton"

    :is="href  ? 'a' : 'button'"
    :class="classes"
    :disabled="disabled || loading"
    :aria-disabled="disabled || loading"
    :aria-label="label"

    :href="href"

    @click="click"
    @blur="blur"
    @focus="focus"
  >
    <credere-spinner
      v-if="loading"
      class="credere-button-loading"
      :variant="spinnerVariant"
    />

    <font-awesome-icon
      v-if="icon"
      :icon="icon"
      class="credere-button-icon"
    />

    <slot />
  </component>
</template>

<style lang="scss">
  @import '@css/mixins/button.scss';

  .credere-button {
    @include credere-button;

    &.size--small {
      @include button-size(
        var(--cordel-font-size-s),
        var(--cordel-font-size-s),
        var(--cordel-spacing-micro) var(--cordel-spacing-xxxs),
        var(--cordel-spacing-nano),
      );
    }

    &.size--medium {
      @include button-size(
        var(--cordel-font-size-m),
        var(--cordel-font-size-s),
        var(--cordel-spacing-xxxs) var(--cordel-spacing-l),
        var(--cordel-spacing-xxxs),
      );
    }

    &.size--large {
      @include button-size(
        var(--cordel-font-size-l),
        var(--cordel-font-size-m),
        var(--cordel-spacing-xs) var(--cordel-spacing-giga),
        var(--cordel-spacing-xxxs),
      );
    }

    &.color--primary {
      @include button-color(
        var(--cordel-color-neutral-darker),
        var(--cordel-color-neutral-heavy),
        var(--cordel-color-constant-light),
      );
    }

    &.color--secondary {
      @include button-color(
        var(--cordel-color-neutral-dark),
        var(--cordel-color-constant-dark),
        var(--cordel-color-constant-light),
      );
    }

    &.color--highlight {
      @include button-color(
        var(--cordel-color-highlight-primary),
        var(--cordel-color-highlight-dark),
        var(--cordel-color-constant-dark),
      );
    }

    &.color--danger {
      @include button-color(
        var(--cordel-color-feedback-danger),
        var(--cordel-color-feedback-danger-dark),
        var(--cordel-color-constant-light),
      );
    }

    &.color--light {
      @include button-color(
        var(--cordel-color-neutral-light),
        var(--cordel-color-neutral-medium),
        var(--cordel-color-constant-dark),
      );
    }

    &.icon-position--right {
      flex-direction: row-reverse;
    }

    &.icon-size--large {
      .credere-button-icon {
        font-size: 1.25em;
      }
    }
  }

  .credere-button-loading {
    bottom: 0;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
  }
</style>
